import React, { useCallback, useState } from 'react'
import {
  Alert,
  Spin,
  Table,
  Tag,
  Typography,
} from '@surecompanies/core_components'
import type { ColumnsType } from 'antd/es/table'
import type { RecentFailureDto } from '../../../../api/dto/get-payment-bot-dashboard.dto'
import { COL_WIDTHS } from '../../shared/constants'
import { formatErrorCode, getProcessTimeBucketLabel } from '../../shared/utils'
import type { CarrierRow } from '../../shared/types'
import type { CarrierFailuresController } from './hooks/useCarrierFailures'
import { useCarrierFailureColumns } from './hooks/useCarrierFailureColumns'
import {
  ResizableTitle,
  withResizableColumns,
} from '../../../shared/ResizableTitle'
import styles from './CarrierDetail.module.scss'

const { Title } = Typography

const DEFAULT_FAILURES_COL_WIDTHS: Record<string, number> = {
  paymentAssignmentId: COL_WIDTHS.taskId,
  carrierName: COL_WIDTHS.carrierName,
  benefitId: COL_WIDTHS.benefitId,
  errorCode: COL_WIDTHS.errorCode,
  insuredName: COL_WIDTHS.insuredName,
  failedAt: COL_WIDTHS.failedAt,
  processTimeSec: COL_WIDTHS.processTimeSec,
  secondAttemptSuccess: COL_WIDTHS.secondAttemptSuccess,
  note: COL_WIDTHS.note,
  logs: COL_WIDTHS.logs,
}

interface Props {
  failures: CarrierFailuresController
  availableCarrierRows: CarrierRow[]
}

// Recent failed attempts: active-filter tags + the paginated, server-sorted
// failures table. All state comes from the useCarrierFailures controller.
// Column widths are stored in state so headers can be dragged to resize.
export function CarrierRecentFailuresTable({
  failures,
  availableCarrierRows,
}: Props) {
  const {
    isAllCarriers,
    selectedErrorCode,
    clearErrorCode,
    selectedCarrierNames,
    setSelectedCarrierNames,
    processTimeBucket,
    setProcessTimeBucket,
    secondAttemptSuccess,
    setSecondAttemptSuccess,
    selectedBenefitId,
    setSelectedBenefitId,
    failurePage,
    setFailurePage,
    failurePageSize,
    setFailurePageSize,
    tableSorter,
    recentFailures,
    failuresTotal,
    failuresFetching,
    failuresIsError,
    failuresError,
    handleFailureTableChange,
  } = failures

  const baseColumns = useCarrierFailureColumns({
    isAllCarriers,
    availableCarrierRows,
    selectedCarrierNames,
    setSelectedCarrierNames,
    setFailurePage,
    tableSorter,
    processTimeBucket,
    secondAttemptSuccess,
    selectedBenefitId,
  })

  const [colWidths, setColWidths] = useState(DEFAULT_FAILURES_COL_WIDTHS)

  const handleResize = useCallback(
    (key: string) => (width: number) =>
      setColWidths((prev) => ({ ...prev, [key]: width })),
    []
  )

  const columns: ColumnsType<RecentFailureDto> = withResizableColumns(
    baseColumns,
    colWidths,
    handleResize
  )

  const scrollX = baseColumns.reduce((sum, col) => {
    const key = col.key as string
    return sum + (colWidths[key] ?? 100)
  }, 0)

  return (
    <div className={styles.section}>
      <div className={styles.sectionHeader} style={{ marginBottom: 12 }}>
        <Title level={5} className={styles.sectionTitle}>
          Recent failed attempts
        </Title>
      </div>
      {(selectedErrorCode ||
        selectedCarrierNames.length > 0 ||
        processTimeBucket ||
        secondAttemptSuccess !== null ||
        selectedBenefitId) && (
        <div className={styles.activeFilterTags}>
          {selectedErrorCode && (
            <Tag
              color="red"
              closable
              onClose={(e) => {
                e.preventDefault()
                clearErrorCode()
              }}
            >
              Error: {formatErrorCode(selectedErrorCode)}
            </Tag>
          )}
          {selectedCarrierNames.map((name) => (
            <Tag
              key={name}
              color="blue"
              closable
              onClose={(e) => {
                e.preventDefault()
                setSelectedCarrierNames((prev) =>
                  prev.filter((n) => n !== name)
                )
                setFailurePage(1)
              }}
            >
              Carrier: {name}
            </Tag>
          ))}
          {processTimeBucket && (
            <Tag
              color="orange"
              closable
              onClose={(e) => {
                e.preventDefault()
                setProcessTimeBucket(null)
                setFailurePage(1)
              }}
            >
              Process time: {getProcessTimeBucketLabel(processTimeBucket)}
            </Tag>
          )}
          {secondAttemptSuccess !== null && (
            <Tag
              color="purple"
              closable
              onClose={(e) => {
                e.preventDefault()
                setSecondAttemptSuccess(null)
                setFailurePage(1)
              }}
            >
              2nd Attempt Success: {secondAttemptSuccess ? 'Success' : 'Failed'}
            </Tag>
          )}
          {selectedBenefitId && (
            <Tag
              color="cyan"
              closable
              onClose={(e) => {
                e.preventDefault()
                setSelectedBenefitId(null)
                setFailurePage(1)
              }}
            >
              Benefit ID: {selectedBenefitId}
            </Tag>
          )}
        </div>
      )}
      {failuresIsError && (
        <Alert
          type="error"
          showIcon
          className={styles.alertMb}
          message="Failed to load recent failed attempts"
          description={failuresError?.message}
          data-testid="carrier-recent-failures-error"
        />
      )}
      <Spin spinning={failuresFetching}>
        <Table<RecentFailureDto>
          columns={columns}
          dataSource={recentFailures}
          rowKey="taskId"
          size="small"
          scroll={{ x: scrollX, y: 450 }}
          onChange={handleFailureTableChange}
          components={{ header: { cell: ResizableTitle } }}
          pagination={{
            current: failurePage,
            pageSize: failurePageSize,
            total: failuresTotal,
            showSizeChanger: true,
            pageSizeOptions: ['25', '50', '100'],
            showTotal: (total) => `${total} records`,
            onChange: (page) => setFailurePage(page),
            onShowSizeChange: (_current, size) => {
              setFailurePageSize(size)
              setFailurePage(1)
            },
          }}
        />
      </Spin>
    </div>
  )
}
